import * as React from 'react'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { BrowseVendorsLink, EventVendorsArea } from '@/components/directory/EventVendorsArea'
import { EventReviews } from '@/components/reviews/EventReviews'
import { EventQuoteRequests } from '@/components/quotes/EventQuoteRequests'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, Banknote, Briefcase, Building2, CalendarDays, ChevronLeft, ExternalLink, History, Plus, Trash2, LayoutDashboard, CalendarClock, ListChecks, Users, FileUp, Files, Image as ImageIcon, ListOrdered, MessageSquareText, NotebookPen, Store, UserX, Waypoints } from 'lucide-react'
import { EventImportPanel } from '@/components/EventImportPanel'
import { EventTimelineBody } from '@/components/EventTimelineBody'
import { CoverPhotoField } from '@/components/CoverPhotoField'
import { RecordHeader } from '@/components/RecordHeader'
import { HoneyBookBookedValue } from '@/components/honeybook/HoneyBookBookedValue'
import { HoneyBookEventNotices } from '@/components/honeybook/HoneyBookEventNotices'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { eventStatusKey } from '@/pages/eventsList'
import { UnconfirmedPlansAlert } from '@/components/UnconfirmedPlansAlert'
import { useEventFeedbackSection } from '@/components/events/EventCoupleFeedback'
import { EventHistory } from '@/components/events/EventHistory'
import { LoseInquiryDialog, LostNotice } from '@/components/events/LostInquiry'
import { RequestedDates } from '@/components/events/RequestedDates'
import { HoldControl } from '@/components/events/HoldControl'
import { HELD_LABEL, useHeldDays } from '@/hooks/useHeldDays'
import { PlanDeleteDialog } from '@/components/PlanDeleteDialog'
import { joinNames } from '@/lib/joinNames'
import { formatEventDate } from '@/lib/eventTime'
import { bookableSpaces, trackIdsOf } from '@lib/helpers/event-track'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { isLiveLifecycle } from '@lib/enums/event-lifecycle.enum'
import { isPlausibleEventDay, MAX_EVENT_DAY, MIN_EVENT_DAY, PHASE_LABEL_MAX } from '@lib/input/event.input'
import { eventPhoto } from '@/lib/eventPhoto'
import {
  useEvent,
  useUpdateEvent,
  useSetGuestCount,
  useCreatePhase,
  useUpdatePhase,
  useDeletePhase,
  useDeleteEvent,
  useEventEditors,
  useTrackAvailability,
} from '@/hooks/useEvents'
import { useEventTimeline } from '@/hooks/useTimeline'
import { useAuth } from '@/auth/AuthContext'
import { can, isStaff } from '@/auth/access'
import { isVendor } from '@/auth/roles'
import { EventClientsPanel } from '@/components/EventClientsPanel'
import { EventNotesEditor } from '@/components/EventNotesEditor'
import { EventGhlPanel } from '@/components/gohighlevel/EventGhlPanel'
import { EventSalesSection } from '@/components/sales/EventSalesSection'
import { ProposalNotice } from '@/components/sales/ProposalNotice'
import { useOrganization } from '@/hooks/useOrganization'
import { useGhlAvailability } from '@/hooks/useGoHighLevel'
import { EventPlannerPanel } from '@/components/EventPlannerPanel'
import { EventVendorsPanel } from '@/components/EventVendorsPanel'
import { ProfileChecklistCard } from '@/components/vendor-profile/ProfileChecklistCard'
import { vendorsByCategory } from '@/lib/eventVendors'
import { EventDocumentsPanel } from '@/components/EventDocumentsPanel'
import { requestErrorMessage } from '@/helpers/apiClient'
import { accountsRemovedLabel } from '@/helpers/eventDelete'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { useCoupleFeedbackPage } from '@/hooks/useCoupleFeedback'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { TimePicker } from '@/components/ui/time-picker'
import { SectionNav, type PageSection } from '@/components/ui/section-nav'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton } from '@/components/ui/states'
import { Select } from '@/components/ui/select'
import { InlineText } from '@/components/ui/inline-text'
import { useToast } from '@/components/ui/toast'
import { clientRetirementFor } from '@lib/helpers/event-client-cleanup'
import type { DeleteEventQuery } from '@lib/input/event.input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { fmt12, timeRangeLabel, toTimeInput, toIsoTime } from '@/lib/eventTime'
import { Facts } from '@/components/ui/facts'
import type { EventDetailDto, EventPhaseDto, EventVendorDto } from '@lib/dto/event.dto'
import { SpaceMark } from '@/components/SpaceMark'
import { spaceHues, type SpaceHue } from '@/lib/spaceHue'
import { DatePicker } from '@/components/ui/date-picker'
import { FormError } from '@/components/ui/form-error'
import { useBookedDays } from '@/hooks/useBookedDays'
import { TrackPicker } from '@/components/TrackPicker'
import type { TrackAvailabilityQuery } from '@lib/input/track-availability.input'
import { furnitureUnits } from '@lib/helpers/stock-units'
import { bookedLabel, todayIso } from '@/lib/bookedDays'

/** The event's name, edited in place (save on blur/Enter). */
function EventName({ event, onError }: { event: EventDetailDto; onError: (m: string | null) => void }) {
  const [name, setName] = React.useState(event.name)
  const update = useUpdateEvent(event.id)
  React.useEffect(() => setName(event.name), [event.name])

  function save(): void {
    const trimmed = name.trim()
    if (!trimmed) {
      setName(event.name)
      return
    }
    if (trimmed !== event.name) update.mutate({ name: trimmed }, { onError: (e) => onError(requestErrorMessage(e)) })
  }

  return (
    <InlineText
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      aria-label="Event name"
    />
  )
}

/**
 * The event's date, on the picker, with the days another event already holds
 * this event's track marked — so a clash shows before the server refuses it.
 *
 * Picking saves at once. This used to be a native date field that saved after
 * a pause in typing, because it reported each keystroke of a year as a date of
 * its own (0002, 0020, 0202, 2027) and saving on change moved the event, its
 * plans and its run of the day through every one of them. A picker only ever
 * produces a whole date. Picking the chosen day again clears it, which the API
 * rules on.
 */
function EventDateInput({
  event,
  value,
  onCommit,
}: {
  event: EventDetailDto
  value: CalendarDay | ''
  onCommit: (day: CalendarDay | '') => void
}) {
  const [month, setMonth] = React.useState<CalendarDay | null>(null)
  const trackIds = trackIdsOf(event)
  const shownMonth = month ?? (value === '' ? todayIso() : value)
  const booked = useBookedDays({ venueId: event.venueId, trackIds, month: shownMonth, excludeEventId: event.id })
  // Another couple's hold warns before booking and never stops it.
  const held = useHeldDays({ venueId: event.venueId, trackIds, month: shownMonth, excludeEventId: event.id })

  return (
    <DatePicker
      id="ev-date"
      value={value}
      onChange={(day) => {
        if (day !== value && (day === '' || isPlausibleEventDay(day))) onCommit(day)
      }}
      min={MIN_EVENT_DAY}
      max={MAX_EVENT_DAY}
      marked={booked}
      markedLabel={bookedLabel(trackIds.length)}
      held={held}
      heldLabel={HELD_LABEL}
      onMonthChange={setMonth}
      className="w-48"
    />
  )
}

/**
 * The tracks the event holds, as the creation form's cards — at a venue that
 * runs concurrent events, where which part of the venue an event takes is a
 * real choice. Picking saves at once, like the date.
 *
 * The cards say whether each lane is free for this event's window, leaving the
 * event itself out of the answer — or every lane it holds would read as taken
 * by itself. The save decides again under a lock, so a taken card stays
 * clickable and the refusal names who has it.
 */
function EventTracksField({
  event,
  onCommit,
}: {
  event: EventDetailDto
  onCommit: (trackIds: string[]) => void
}) {
  const trackIds = trackIdsOf(event)
  const day = event.date
  const ask: TrackAvailabilityQuery =
    day === null
      ? { venueId: event.venueId, excludeEventId: event.id }
      : {
          venueId: event.venueId,
          excludeEventId: event.id,
          date: day,
          ...(event.startsAt !== null ? { startsAt: event.startsAt } : {}),
          ...(event.endsAt !== null ? { endsAt: event.endsAt } : {}),
        }
  const availability = useTrackAvailability(ask)

  return (
    <Field label="Tracks">
      <div className="space-y-2">
        {trackIds.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Not on a track yet. Until it has one, another event can be booked into its rooms at the same time.
          </p>
        ) : null}
        <TrackPicker
          venue={event.venue}
          value={trackIds}
          onChange={onCommit}
          availability={availability.data?.tracks}
          loading={availability.isFetching}
          failed={availability.isError}
          name="event-detail-track"
        />
      </div>
    </Field>
  )
}

/**
 * The details of an event HoneyBook manages (docs/honeybook.plan.md, 4.7): its
 * name, day, times and guests read as HoneyBook has them, changed there. The
 * tracks it is on, and what the couple booked (phase 6), stay the venue's.
 */
function ManagedSchedule({ event, canEdit, onError }: { event: EventDetailDto; canEdit: boolean; onError: (m: string | null) => void }) {
  const update = useUpdateEvent(event.id)
  const honeybook = event.honeybook
  return (
    <div className="space-y-4">
      <p className="m-0 text-sm text-ink-muted">
        Managed by HoneyBook: change its name, day, times and guests there, and they update here within the hour.
        {honeybook?.openUrl == null ? null : (
          <>
            {' '}
            <a href={honeybook.openUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-ink underline underline-offset-2">
              Open in HoneyBook <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </>
        )}
      </p>
      <ScheduleBody event={event} canEdit={false} onError={onError} />
      {honeybook?.usesDefaultTimes === true ? (
        <p className="m-0 text-sm text-ink-muted">The venue’s usual times, until HoneyBook has the project’s.</p>
      ) : null}
      {canEdit && event.venue.supportsConcurrentEvents ? (
        <EventTracksField
          event={event}
          onCommit={(trackIds) => {
            onError(null)
            update.mutate({ trackIds }, { onError: (e) => onError(requestErrorMessage(e)) })
          }}
        />
      ) : null}
      <HoneyBookBookedValue key={honeybook?.bookedValueCents ?? 'none'} event={event} canEdit={canEdit} />
    </div>
  )
}

/** The venue's own schedule; an event once made from a HoneyBook booking keeps its typed booked value with it. */
function OwnSchedule({ event, canEdit, onError }: { event: EventDetailDto; canEdit: boolean; onError: (m: string | null) => void }) {
  if (event.honeybook === null) return <ScheduleBody event={event} canEdit={canEdit} onError={onError} />
  return (
    <div className="space-y-4">
      <ScheduleBody event={event} canEdit={canEdit} onError={onError} />
      <HoneyBookBookedValue key={event.honeybook.bookedValueCents ?? 'none'} event={event} canEdit={canEdit} />
    </div>
  )
}

/** Date + start/end time + guest count, each committing on change/blur; read as facts without Edit on Events. */
function ScheduleBody({
  event,
  canEdit,
  onError,
}: {
  event: EventDetailDto
  /** Changing the name, the day, its times, the guest count and the tracks. */
  canEdit: boolean
  onError: (m: string | null) => void
}) {
  const update = useUpdateEvent(event.id)
  // An unset count (stored as 0) shows as an empty field, as the header says
  // "Guest count not set": the field showed 0 beside it.
  const shownGuests = (n: number): string => (n > 0 ? String(n) : '')
  const [guests, setGuests] = React.useState(shownGuests(event.guestCount))
  React.useEffect(() => setGuests(shownGuests(event.guestCount)), [event.guestCount])
  // Date and the two clocks are one window on the server. A later save fills
  // whichever clock this request left out from the row it read, so the next
  // edit waits until the previous one has been written.
  const windowChain = React.useRef(Promise.resolve())
  // An inquiry's requested dates; a lost one shows them without the controls.
  const undated = event.lifecycle === 'enquiry' || event.lifecycle === 'lost'

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <p className="text-lg font-semibold">{event.name}</p>
        {undated ? <RequestedDates event={event} canEdit={false} onError={onError} /> : null}
        <Facts
          layout="stack"
          facts={[
            { label: 'Date', value: event.date ? formatEventDate(event.date) : null, empty: 'Date not set' },
            {
              label: 'Time',
              value: event.startsAt && event.endsAt ? `${fmt12(event.startsAt)} – ${fmt12(event.endsAt)}` : null,
              empty: 'Times not set',
            },
            { label: 'Guests', value: event.guestCount > 0 ? String(event.guestCount) : null, empty: 'Guest count not set' },
          ]}
        />
      </div>
    )
  }

  const commit = (input: Parameters<typeof update.mutate>[0]): void => {
    onError(null)
    update.mutate(input, { onError: (e) => onError(requestErrorMessage(e)) })
  }
  const commitWindow = (input: Parameters<typeof update.mutate>[0]): void => {
    onError(null)
    windowChain.current = windowChain.current.catch(() => undefined).then(async () => {
      try {
        await update.mutateAsync(input)
      } catch (cause) {
        onError(requestErrorMessage(cause))
      }
    })
  }
  const dateValue = event.date ?? ''

  return (
    <div className="space-y-4">
      {/* The name lives with the date: the hero shows it, this is where it is
          changed, and both are the event's own details. */}
      <EventName event={event} onError={onError} />
        {undated ? <RequestedDates event={event} canEdit={event.lifecycle === 'enquiry'} onError={onError} /> : null}
        {event.lifecycle === 'enquiry' ? <HoldControl event={event} onError={onError} /> : null}
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Date" htmlFor="ev-date">
            <EventDateInput event={event} value={dateValue} onCommit={(day) => commitWindow({ date: day === '' ? null : day })} />
          </Field>
          <Field label="Start time">
            <TimePicker
              value={toTimeInput(event.startsAt)}
              disabled={!event.date}
              ariaLabel="Event start time"
              onChange={(v) => commitWindow({ startsAt: toIsoTime(event.date, v) })}
            />
          </Field>
          <Field label="End time">
            <TimePicker
              value={toTimeInput(event.endsAt)}
              disabled={!event.date}
              ariaLabel="Event end time"
              onChange={(v) => commitWindow({ endsAt: toIsoTime(event.date, v) })}
            />
          </Field>
          <Field label="Guests" htmlFor="ev-guests">
            <Input
              id="ev-guests"
              type="number"
              min={0}
              value={guests}
              placeholder="Not set"
              onChange={(e) => setGuests(e.target.value)}
              onBlur={() => {
                const n = Number.parseInt(guests, 10)
                if (Number.isFinite(n) && n >= 0 && n !== event.guestCount) commit({ guestCount: n })
              }}
              className="w-24"
            />
          </Field>
        </div>
        {!event.date ? <p className="text-sm text-ink-muted">Set a date to add start and end times.</p> : null}
        {/* A window that crosses midnight is stored ending on the next day (see
            `eventWindow`); the two pickers alone read as "4:00 PM to 2:00 PM". */}
        {event.startsAt !== null && event.endsAt !== null && event.endsAt.slice(0, 10) !== event.startsAt.slice(0, 10) ? (
          <p className="text-sm text-ink-muted">Ends the next day.</p>
        ) : null}
        {event.venue.supportsConcurrentEvents ? (
          <EventTracksField event={event} onCommit={(trackIds) => commit({ trackIds })} />
        ) : null}
      </div>
  )
}

/** One phase row: its space's mark, label, space, its time window (retimed in place), and delete. */
function PhaseRow({
  event,
  phase,
  hue,
  canEdit,
  onError,
}: {
  event: EventDetailDto
  phase: EventPhaseDto
  hue: SpaceHue | undefined
  /** Retiming the phase and deleting it. */
  canEdit: boolean
  onError: (m: string | null) => void
}) {
  const update = useUpdatePhase(event.id)
  const del = useDeletePhase(event.id)
  const [confirming, setConfirming] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)
  const pieces = furnitureUnits(phase.objects)
  const retime = (input: { startsAt?: string | null; endsAt?: string | null }): void => {
    onError(null)
    update.mutate({ phaseId: phase.id, input }, { onError: (e) => onError(requestErrorMessage(e)) })
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3">
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        <SpaceMark hue={hue} transition={phase.type === 'transition'} />
        <span className="min-w-0 truncate text-sm font-medium">{phase.label}</span>
      </span>
      <span className="shrink-0 text-xs text-ink-muted">{phase.space.name}</span>
      {phase.frozen ? <Badge tone="neutral">Locked</Badge> : null}
      {!phase.frozen && !canEdit ? (
        <span className="shrink-0 font-mono text-xs tabular-nums text-ink-muted">
          {phase.startsAt && phase.endsAt ? `${fmt12(phase.startsAt)} – ${fmt12(phase.endsAt)}` : 'Times not set'}
        </span>
      ) : null}
      {!phase.frozen && canEdit ? (
        <div className="flex items-center gap-1.5">
          <TimePicker
            value={toTimeInput(phase.startsAt)}
            disabled={!event.date}
            ariaLabel={`${phase.label} start time`}
            triggerClassName="h-8 gap-1.5 px-2 text-control"
            onChange={(v) => retime({ startsAt: toIsoTime(event.date, v) })}
          />
          <span className="text-xs text-ink-muted">–</span>
          <TimePicker
            value={toTimeInput(phase.endsAt)}
            disabled={!event.date}
            ariaLabel={`${phase.label} end time`}
            triggerClassName="h-8 gap-1.5 px-2 text-control"
            onChange={(v) => retime({ endsAt: toIsoTime(event.date, v) })}
          />
          <button
            type="button"
            aria-label={`Delete ${phase.label}`}
            disabled={del.isPending}
            onClick={() => {
              onError(null)
              setDeleteError(null)
              setConfirming(true)
            }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : null}
      <PlanDeleteDialog
        eventId={event.id}
        plan={confirming ? { id: phase.id, label: phase.label, pieces } : null}
        onOpenChange={setConfirming}
        busy={del.isPending}
        error={deleteError}
        onConfirm={() =>
          del.mutate(
            { phaseId: phase.id },
            { onSuccess: () => setConfirming(false), onError: (e) => setDeleteError(requestErrorMessage(e)) },
          )
        }
      />
    </div>
  )
}

/** Phases list with an inline add form (label + space). Times are set per row after. */
function PhasesBody({
  event,
  canEdit,
  onError,
}: {
  event: EventDetailDto
  /** Adding, retiming and deleting phases. */
  canEdit: boolean
  onError: (m: string | null) => void
}) {
  const create = useCreatePhase(event.id)
  // Spaces bookable for this event: those on any of its tracks, plus the
  // unassigned ones (all of them when it is venue-wide). See `bookableSpaces`.
  const spaces = bookableSpaces(event.venue.spaces, trackIdsOf(event))
  const hues = spaceHues(event.venue.spaces)

  const [label, setLabel] = React.useState('')
  const [spaceId, setSpaceId] = React.useState('')

  function add(): void {
    if (label.trim() === '' || spaceId === '') return
    onError(null)
    create.mutate(
      { label: label.trim(), venueSpaceId: spaceId, type: 'main' },
      {
        onSuccess: () => {
          setLabel('')
          setSpaceId('')
        },
        onError: (e) => onError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <div>
      {event.phases.length === 0 ? (
        <p className="text-sm text-ink-muted">No phases yet.</p>
      ) : (
        <div className="divide-y divide-line rounded-md border border-line">
          {event.phases.map((p) => (
            <PhaseRow key={p.id} event={event} phase={p} hue={hues.get(p.venueSpaceId)} canEdit={canEdit} onError={onError} />
          ))}
        </div>
      )}

      {canEdit ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field size="sm" label="Phase name" htmlFor="new-phase-label" className="min-w-[10rem] flex-1">
            <Input
              id="new-phase-label"
              value={label}
              maxLength={PHASE_LABEL_MAX}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Dinner"
              className="h-9"
            />
          </Field>
          <Field size="sm" label="Space" htmlFor="new-phase-space" className="min-w-[9rem]">
            <Select id="new-phase-space" size="sm" value={spaceId} onChange={(e) => setSpaceId(e.target.value)}>
              <option value="">Select…</option>
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button pending={create.isPending} type="button" onClick={add} disabled={label.trim() === '' || spaceId === ''}>
            <Plus className="h-4 w-4" />
            Add phase
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/** The planner on the event as the couple reads it: the company, and its contact to write to. */
function plannedBy(event: EventDetailDto): React.ReactNode {
  const planner = event.planner
  if (!planner) return <span className="text-ink-muted">No planner</span>
  const contact = planner.contact
  if (!contact) return planner.companyName
  return (
    <span className="block">
      {planner.companyName}
      <span className="block text-xs text-ink-muted">
        {contact.name} ·{' '}
        <a href={`mailto:${contact.email}`} className="underline underline-offset-2 hover:text-ink">
          {contact.email}
        </a>
      </span>
    </span>
  )
}

/**
 * One company as the couple or their planner reads it: its name, which opens
 * its profile, and its contact to write to when it has one.
 */
function vendorLine(vendor: EventVendorDto): React.ReactNode {
  const contact = vendor.connection === 'assigned' ? vendor.contact : null
  const name = <VendorProfileLink vendorId={vendor.vendorId} name={vendor.companyName} />
  if (!contact) return name
  return (
    <>
      {name}
      <span className="block text-xs text-ink-muted">
        {contact.name} ·{' '}
        <a href={`mailto:${contact.email}`} className="underline underline-offset-2 hover:text-ink">
          {contact.email}
        </a>
      </span>
    </>
  )
}

/** The event's vendors other than its planner, by category. */
function EventVendorList({ event }: { event: EventDetailDto }) {
  const categories = vendorsByCategory(event.vendors)
  if (categories.length === 0) return <p className="text-sm text-ink-muted">No other vendors yet.</p>
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:max-w-md">
      {categories.map((category) => (
        <React.Fragment key={category.label}>
          <dt className="text-ink-muted">{category.label}</dt>
          <dd>
            {category.vendors.map((vendor) => (
              <span key={vendor.connection === 'assigned' ? vendor.assignmentId : vendor.vendorId} className="block">
                {vendorLine(vendor)}
              </span>
            ))}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

/**
 * An event's own page: its name, schedule (date + time window), and phases —
 * everything except the floor plan itself, which opens full-screen from here.
 */
/**
 * A client's view of their event: schedule, guests, phases — and the run of the
 * day, which they may edit.
 *
 * It was entirely read-only, on the reasoning that a client's editing happens in
 * the floor plan. The timeline is the first thing they change on this page, so
 * that is no longer true.
 */
/**
 * The guest count on the couple's and their planner's view: theirs to set, the
 * one detail of the event that is (docs/planner-vendors.md, "Who can do what").
 * A planner had nowhere to put it. Read-only once the event is over, as the
 * timeline is, and where HoneyBook keeps the count for the venue.
 */
function GuestCount({ event }: { event: EventDetailDto }) {
  const setGuestCount = useSetGuestCount(event.id)
  const [guests, setGuests] = React.useState(event.guestCount > 0 ? String(event.guestCount) : '')
  const [error, setError] = React.useState<string | null>(null)
  React.useEffect(() => setGuests(event.guestCount > 0 ? String(event.guestCount) : ''), [event.guestCount])

  if (event.hasFinished || event.guestCountLocked) {
    return (
      <span>
        {event.guestCount > 0 ? String(event.guestCount) : 'Not set'}
        {event.guestCountLocked ? <span className="text-ink-muted"> · kept by the venue</span> : null}
      </span>
    )
  }

  function commit(): void {
    const n = guests.trim() === '' ? 0 : Number.parseInt(guests, 10)
    if (!Number.isFinite(n) || n < 0 || n === event.guestCount) return
    setError(null)
    setGuestCount.mutate({ guestCount: n }, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <div className="space-y-1">
      <Input
        type="number"
        size="sm"
        min={0}
        inputMode="numeric"
        value={guests}
        placeholder="How many?"
        aria-label="Guest count"
        onChange={(e) => setGuests(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className="w-28"
      />
      {error ? <FormError size="sm">{error}</FormError> : null}
    </div>
  )
}

/**
 * On a finished booking, telling the venue about their experience is the action.
 * Once they have sent, it becomes a link back to what they sent.
 */
function CoupleDayAsk({ eventId, venueName }: { eventId: string; venueName: string }) {
  const page = useCoupleFeedbackPage(eventId)
  if (page.isSuccess && page.data.answer !== null) {
    return (
      <p className="text-sm">
        <Link to={`/events/${eventId}/feedback`} className="font-medium text-accent-ink underline-offset-2 hover:underline">
          What you told {venueName}
        </Link>
      </p>
    )
  }
  if (page.isPending) return null
  return (
    <Callout
      title="Tell us about your experience"
      action={
        <Button asChild>
          <Link to={`/events/${eventId}/feedback`}>Write to {venueName}</Link>
        </Button>
      }
    >
      Only {venueName} reads what you write. It isn’t published.
    </Callout>
  )
}

function ClientEventSummary({ event, planner }: { event: EventDetailDto; planner: boolean }) {
  const hues = spaceHues(event.venue.spaces)
  // The couple's times read as staff's do ("4:00 PM – 11:00 PM"). They were the
  // picker's 24-hour values, "16:00 – 23:00", and "TBD" where nothing was set.
  const dateText = event.date ? formatEventDate(event.date) : 'Date not set'
  const endsNextDay = event.startsAt !== null && event.endsAt !== null && event.endsAt.slice(0, 10) !== event.startsAt.slice(0, 10)
  const timeText =
    event.startsAt && event.endsAt ? `${timeRangeLabel(event.startsAt, event.endsAt)}${endsNextDay ? ', ending the next day' : ''}` : 'Time not set'
  return (
    <div className="space-y-6 border-t border-line pt-6">
      {!planner && event.lifecycle === 'booked' && event.hasFinished ? (
        <CoupleDayAsk eventId={event.id} venueName={event.venue.name} />
      ) : null}
      <ProposalNotice eventId={event.id} planner={planner} />
      {/* The planner's own profile, while it has steps to go (docs/vendor-growth.spec.md). */}
      {planner ? <ProfileChecklistCard /> : null}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:max-w-md">
        <dt className="text-ink-muted">Date</dt>
        <dd>{dateText}</dd>
        <dt className="text-ink-muted">Time</dt>
        <dd>{timeText}</dd>
        <dt className="text-ink-muted">Guests</dt>
        <dd>
          <GuestCount event={event} />
        </dd>
        {/* Who is planning it, and who to deal with there (docs/planner-vendors.md,
            "What each person sees"). Kept when there is none, like every fact here. */}
        <dt className="text-ink-muted">Planned by</dt>
        <dd>{plannedBy(event)}</dd>
      </dl>
      <div className="space-y-2">
        {/* The couple's list of the event's other vendors, by category
            (decision 18). Kept when there are none, like the facts above. The
            planner adds and takes them off, all but itself, while the event is
            ahead and going ahead (docs/vendor-growth.spec.md); the API holds
            the same line. */}
        <p className="text-sm font-medium">Vendors</p>
        {planner ? (
          <>
            <EventVendorsPanel event={event} canEdit={!event.hasFinished && isLiveLifecycle(event.lifecycle)} forPlanner />
            {/* The planner browses the venue's vendors too. */}
            <BrowseVendorsLink eventId={event.id} venueName={event.venue.name} />
          </>
        ) : (
          <EventVendorList event={event} />
        )}
      </div>
      {/* Once a booking is over, the couple and the planner review the companies that worked it
          (docs/vendor-growth.spec.md, "Reviews"). The section's buttons follow the server's answer. */}
      {event.hasFinished && event.lifecycle === 'booked' ? <EventReviews eventId={event.id} planner={planner} /> : null}
      {/* The couple finds the venue's vendors for what is still open, while the event is ahead. */}
      {!planner && !event.hasFinished && isLiveLifecycle(event.lifecycle) ? <EventVendorsArea event={event} /> : null}
      <div className="space-y-2">
        {/* Who has asked whom for a quote; asked from the directory, while the event is ahead. */}
        <p className="text-sm font-medium">Quote requests</p>
        <EventQuoteRequests
          eventId={event.id}
          asCouple={!planner}
          ask={!event.hasFinished && isLiveLifecycle(event.lifecycle) ? 'directory' : null}
        />
      </div>
      <div className="space-y-2">
        {/* The same component the admin section renders. A client edits their own
            event's run of the day for the reason they may already add furniture
            to its plan — and one implementation means the two views cannot
            disagree about it. See docs/event-timeline.md. */}
        <p className="text-sm font-medium">Timeline</p>
        <EventTimelineBody event={event} canEdit={!event.hasFinished} />
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Phases</p>
        <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
          {event.phases.map((p) => {
            const s = toTimeInput(p.startsAt)
            const e = toTimeInput(p.endsAt)
            return (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <SpaceMark hue={hues.get(p.venueSpaceId)} transition={p.type === 'transition'} />
                <span className="min-w-0 flex-1 truncate font-medium">{p.label}</span>
                <span className="text-xs text-ink-muted">
                  {p.space.name}
                  {s && e ? ` · ${s}–${e}` : ''}
                </span>
              </li>
            )
          })}
        </ul>
        <p className="text-xs text-ink-muted">Use “Open floor plan” to arrange furniture for each phase.</p>
      </div>
      {/* A planner reads the event's documents and adds to them; the couple's
          page never showed them. Removing one stays the venue's. */}
      {planner ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">Documents</p>
          <EventDocumentsPanel event={event} canEdit canRemove={false} />
        </div>
      ) : null}
    </div>
  )
}

/** An event's cover photograph — the same permission as the rest of the event. */
function EventPhotoBody({
  event,
  canEdit,
  onError,
}: {
  event: EventDetailDto
  /** Changing the photograph. */
  canEdit: boolean
  onError: (m: string | null) => void
}) {
  const update = useUpdateEvent(event.id)
  return (
    <CoverPhotoField
      disabled={!canEdit}
      coverImageUrl={event.coverImageUrl}
      kind="event"
      onSave={async (coverImageUrl) => {
        onError(null)
        await update.mutateAsync({ coverImageUrl })
      }}
    />
  )
}

/** "3 documents", "1 document". */
function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`
}

/**
 * What deleting the event takes with it, named rather than summed up as
 * "everything": its plans (confirmed ones counted, since those are the
 * agreed setup), its documents and its run of the day — and who has a plan
 * open right now, since they lose it mid-edit. Mounted only while the dialog is
 * open, so the reads it makes are for this moment.
 */
function EventDeleteSummary({ event }: { event: EventDetailDto }) {
  const editors = useEventEditors(event.id, true)
  const timeline = useEventTimeline(event.id)
  const confirmed = event.phases.filter((p) => p.lockedAt !== null).length
  const names = [...new Set(editors.data?.plans.flatMap((p) => p.names) ?? [])]
  const lines: string[] = []
  if (event.phases.length > 0) {
    const one = event.phases.length === 1
    const plans = one ? '1 plan and its furniture' : `${String(event.phases.length)} plans and their furniture`
    let settled = ''
    if (confirmed > 0) settled = one ? ', which is confirmed' : `, ${String(confirmed)} of them confirmed`
    lines.push(`${plans}${settled}`)
  }
  if (event.documents.length > 0) lines.push(count(event.documents.length, 'document', 'documents'))
  const entries = timeline.data?.total ?? 0
  if (entries > 0) lines.push(`${count(entries, 'entry', 'entries')} in the run of the day`)
  if (lines.length === 0 && names.length === 0) return null

  return (
    <div className="space-y-2 text-sm">
      {lines.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      ) : null}
      {names.length > 0 ? (
        <p className="text-danger-ink">
          {joinNames(names)} {names.length === 1 ? 'has' : 'have'} a plan open right now and will lose it.
        </p>
      ) : null}
    </div>
  )
}

export function EventDetailPage() {
  const { eventId } = useParams()
  const id = eventId ?? ''
  const navigate = useNavigate()
  const eventQuery = useEvent(id)
  const deleteEvent = useDeleteEvent()
  const { user } = useAuth()
  // Only staff see the sections, and change them with Edit on Events; the
  // GoHighLevel one only where the event's venue has GoHighLevel connected, since
  // a company turns it on for the venues that use it.
  const admin = isStaff(user)
  const canEdit = can(user, 'events:edit')
  const feedbackSection = useEventFeedbackSection(id, eventQuery.data ?? null, canEdit)
  const companyHasGhl = (useOrganization().data?.features.includes('gohighlevel') ?? false) && admin
  const ghlAvailability = useGhlAvailability(eventQuery.data?.venueId ?? '', companyHasGhl)
  const hasGhl = companyHasGhl && ghlAvailability.data?.connected === true
  const { toast } = useToast()
  const [error, setError] = React.useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [loseOpen, setLoseOpen] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)
  // What to do with the client contacts on the event. Keeping them is the default:
  // deleting an event should never quietly delete people.
  const [deleteClients, setDeleteClients] = React.useState<DeleteEventQuery['clients']>('keep')

  if (eventQuery.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (eventQuery.isError) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="h-8 w-8 text-danger-ink" />
        <p className="text-sm text-ink-muted">{requestErrorMessage(eventQuery.error)}</p>
        <Button variant="ghost" size="sm" asChild>
          <Link to={isVendor(user) ? '/vendor/events' : '/events'}>Back to events</Link>
        </Button>
      </div>
    )
  }

  const event = eventQuery.data
  const hero = eventPhoto(event)
  // Made from a HoneyBook booking it still manages: its name, day, times, guests and couple are HoneyBook's.
  const managed = event.honeybook?.managed === true
  const sections: PageSection[] = [
    {
      id: 'schedule',
      label: 'Details',
      icon: CalendarClock,
      description:
        'The event’s name, the day it is held, and its time window. Every phase must fall within that window.',
      content: managed ? (
        <ManagedSchedule event={event} canEdit={canEdit} onError={setError} />
      ) : (
        <OwnSchedule event={event} canEdit={canEdit} onError={setError} />
      ),
    },
    {
      id: 'notes',
      label: 'Notes',
      icon: NotebookPen,
      description: 'For venue staff only. Clients, planners and vendors never see these.',
      content: <EventNotesEditor key={event.notes ?? ''} event={event} canEdit={canEdit} />,
    },
    {
      id: 'photo',
      label: 'Photo',
      icon: ImageIcon,
      description: 'The photograph that represents this event wherever it is listed.',
      content: <EventPhotoBody event={event} canEdit={canEdit} onError={setError} />,
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: ListOrdered,
      description:
        'The run of the day — what happens and when. Seeded from the venue\u2019s default timeline when the event was dated; edit it here, or start again from another template. Every vendor on the event sees it.',
      content: <EventTimelineBody event={event} canEdit={!event.hasFinished && canEdit} />,
    },
    {
      id: 'phases',
      label: 'Phases',
      icon: ListChecks,
      description: 'Timed segments of the day, each in one space with its own floor plan. Retime a phase, or add one below.',
      content: <PhasesBody event={event} canEdit={canEdit} onError={setError} />,
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: Users,
      description: managed
        ? 'The couple, from HoneyBook. They can log in to view this event and arrange its floor plan.'
        : 'People who can log in to view this event and arrange its floor plan.',
      content: (
        <EventClientsPanel
          event={event}
          canEdit={canEdit && !managed}
          canManageClients={can(user, 'clients:edit')}
          canFindClients={can(user, 'clients:view')}
        />
      ),
    },
    ...(feedbackSection ? [feedbackSection] : []),
    // Proposals, for staff who see Sales (docs/sales.spec.md).
    ...(can(user, 'sales:view')
      ? [
          {
            id: 'sales',
            label: 'Sales',
            icon: Banknote,
            description: 'The proposals sent to this couple, and sending one: what’s offered, the contract, and how it’s paid.',
            content: <EventSalesSection event={event} />,
          },
        ]
      : []),
    ...(hasGhl
      ? [
          {
            id: 'gohighlevel',
            label: 'GoHighLevel',
            icon: Waypoints,
            description: 'The GoHighLevel lead this event came from.',
            content: <EventGhlPanel event={event} canEdit={canEdit} />,
          },
        ]
      : []),
    {
      id: 'planner',
      label: 'Planner',
      icon: Briefcase,
      description:
        'The company planning this event. Its people see the event as its clients do, arrange its floor plan, and add documents.',
      content: <EventPlannerPanel event={event} canEdit={canEdit} />,
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: Store,
      description:
        'Everyone else working the event: a florist, a photographer, and any company whose furniture is rented for it. They see the event, its timeline and its floor plans, and change nothing.',
      content: <EventVendorsPanel event={event} canEdit={canEdit} />,
    },
    {
      id: 'quotes',
      label: 'Quote requests',
      icon: MessageSquareText,
      description: 'What the couple, its planner and your staff have asked vendors for, and when.',
      content: (
        <EventQuoteRequests
          eventId={event.id}
          asCouple={false}
          // Staff choose from the venue's vendors, which takes seeing them.
          ask={canEdit && can(user, 'vendors:view') && !event.hasFinished && isLiveLifecycle(event.lifecycle) ? 'dialog' : null}
        />
      ),
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: Files,
      description: 'BEOs, contracts, proposals, and other files for this event. Link a URL or upload a file.',
      content: <EventDocumentsPanel event={event} canEdit={canEdit} />,
    },
    // Importing replaces a plan, so it is there only for those who can change one.
    ...(canEdit
      ? [
          {
            id: 'import',
            label: 'Import',
            icon: FileUp,
            description:
              'Paste a legacy layout SVG for one space. Matches or creates furniture, tops up inventory, and replaces that space’s phase plan.',
            content: <EventImportPanel event={event} />,
          },
        ]
      : []),
    // Who booked, canceled, lost or reopened it, and why; who changed the guests or the vendors.
    ...((event.stateChanges !== undefined && event.stateChanges.length > 0) || event.edits.length > 0
      ? [
          {
            id: 'history',
            label: 'History',
            icon: History,
            description: 'Changes to this event’s status, guest count and vendors: who made each, when, and why.',
            content: (
              <EventHistory
                changes={event.stateChanges ?? []}
                honeybook={event.honeybook?.changes}
                edits={event.edits}
                timeZone={event.venue.timezone}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <PageContainer width="wide" className="space-y-6">
      {/* A vendor's events are its own list; the organization's is not theirs to open. */}
      <Link
        to={isVendor(user) ? '/vendor/events' : '/events'}
        className="inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4" /> Events
      </Link>

      {/* An event's own photograph if it has one, otherwise its venue's — an
          event happens somewhere, and that somewhere has been photographed. */}
      <RecordHeader
        kind="Event"
        badge={
          event.honeybook === null ? (
            <StatusBadge status={eventStatusKey(event)} />
          ) : (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <StatusBadge status={eventStatusKey(event)} />
              <Badge tone={event.honeybook.paused ? 'warning' : 'neutral'}>{event.honeybook.paused ? 'HoneyBook updates paused' : 'HoneyBook'}</Badge>
            </span>
          )
        }
        title={event.name}
        src={hero.src}
        focalPoint={hero.focalPoint}
        facts={[
          { icon: <Building2 />, label: 'Venue', value: event.venueName, empty: 'No venue' },
          {
            icon: <CalendarDays />,
            label: 'Date',
            value: event.date ? formatEventDate(event.date) : null,
            empty: 'Date not set',
          },
          {
            icon: <Users />,
            label: 'Guests',
            value: event.guestCount > 0 ? `${String(event.guestCount)} guests` : null,
            empty: 'Guest count not set',
          },
        ]}
        actions={
          <>
        <Button asChild variant="outline">
          <Link to={`/events/${event.id}/plan`}>
            <LayoutDashboard className="h-4 w-4" /> Open floor plan
            {/* The detail payload has the phases themselves, so the count comes
                from them rather than from a summary that could disagree. */}
            {/* `frozen` is the server's own judgment — locked, or settled by
                the date passing — so a plan that can no longer be confirmed
                does not ask to be. */}
            <UnconfirmedPlansAlert
              unconfirmed={event.phases.filter((p) => !p.frozen && p.lockedAt === null).length}
              total={event.phases.length}
            />
          </Link>
        </Button>
        {canEdit && event.lifecycle === 'enquiry' ? (
          <Button type="button" variant="outline" onClick={() => setLoseOpen(true)}>
            <UserX className="h-4 w-4" />
            Mark lost
          </Button>
        ) : null}
        {canEdit && can(user, 'deleteEvents') && !managed ? (
          <Button
            type="button"
            variant="outline"
            className="text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
            onClick={() => {
              setDeleteError(null)
              setDeleteOpen(true)
            }}
          >
            <Trash2 className="h-4 w-4" />
            Delete event
          </Button>
        ) : null}
          </>
        }
      />

      {admin && event.lifecycle === 'lost' ? <LostNotice event={event} canEdit={canEdit} /> : null}
      {event.honeybook === null ? null : <HoneyBookEventNotices honeybook={event.honeybook} />}

      {error ? <FormError>{error}</FormError> : null}

      {admin ? (
        <SectionNav sections={sections} ariaLabel="Event sections" />
      ) : (
        <ClientEventSummary event={event} planner={isVendor(user)} />
      )}

      {canEdit && event.lifecycle === 'enquiry' ? (
        <LoseInquiryDialog event={event} open={loseOpen} onOpenChange={setLoseOpen} />
      ) : null}

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open)
          if (!open) {
            setDeleteError(null)
            setDeleteClients('keep')
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete event?</DialogTitle>
            <DialogDescription>
              Remove “{event.name}” and everything on it: its rentals and brought-in furniture go too. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <EventDeleteSummary event={event} />
          {event.clients.length > 0 ? (
            <ChoiceGroup
              legend={event.clients.length === 1 ? 'The client on this event' : `The ${event.clients.length} clients on this event`}
              hint={
                <ul className="m-0 list-none space-y-1.5 rounded-lg border border-line p-3">
                  {event.clients.map((c) => (
                    <li key={c.id} className="flex items-start justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate text-ink">{c.name}</p>
                        <p className="truncate text-xs text-ink-muted">{c.email}</p>
                      </div>
                      {deleteClients === 'remove' ? (
                        <span className="shrink-0">
                          {clientRetirementFor(c.status) === 'delete' ? (
                            <Badge tone="danger">Delete</Badge>
                          ) : (
                            <Badge tone="warning">Deactivate</Badge>
                          )}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              }
            >
              <ChoiceCard
                type="radio"
                name="delete-clients"
                checked={deleteClients === 'keep'}
                onSelect={() => setDeleteClients('keep')}
                title="Keep their accounts"
              />
              <ChoiceCard
                type="radio"
                name="delete-clients"
                checked={deleteClients === 'remove'}
                onSelect={() => setDeleteClients('remove')}
                title="Remove their accounts too"
                description="An invitation nobody accepted is withdrawn, which frees that email address to be invited again. An account someone has signed into is deactivated instead — deleting it would take their history with it. Anyone who is also a contact on another event is kept either way."
              />
            </ChoiceGroup>
          ) : null}
          {deleteError ? <FormError>{deleteError}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeleteError(null)
                deleteEvent.mutate(
                  { eventId: event.id, clients: deleteClients },
                  {
                    onSuccess: (result) => {
                      const removed = accountsRemovedLabel(result)
                      // An account someone can no longer sign into is worth saying out
                      // loud, even though the delete itself succeeded quietly.
                      if (removed) toast(removed, { variant: 'warning' })
                      navigate('/events')
                    },
                    onError: (e) => setDeleteError(requestErrorMessage(e)),
                  },
                )
              }}
              pending={deleteEvent.isPending}
            >
              Delete event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
